import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#rankOutOfStockItems();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  async #rankOutOfStockItems() {
    const { grid } = this.refs;

    if (!grid) return;

    try {
      const currentPage = this.#getCurrentPage();
      const totalPages = Number(grid.dataset.lastPage || '1');
      const pageData = await Promise.all(
        Array.from({ length: totalPages }, async (_, index) => {
          const page = index + 1;
          const pageGrid = page === currentPage ? grid : await this.#fetchGridForPage(page);

          if (!pageGrid) return null;

          return this.#extractPageData(pageGrid, page);
        })
      );

      const validPageData = pageData.filter(Boolean);
      const pageOneData = validPageData.find((page) => page.page === 1);
      const pageSize = Math.max(...validPageData.map((page) => page.cards.length), 0);
      const allCards = validPageData
        .sort((left, right) => left.page - right.page)
        .flatMap((page) => page.cards);

      if (pageSize < 1 || allCards.length < 2) return;

      const inStockCards = allCards.filter((card) => card.dataset.productAvailable !== 'false');
      const outOfStockCards = allCards.filter((card) => card.dataset.productAvailable === 'false');

      if (outOfStockCards.length === 0) return;

      const globallyRankedCards = [...inStockCards, ...outOfStockCards];

      for (let page = 1; page <= totalPages; page += 1) {
        const pageStartIndex = (page - 1) * pageSize;
        const pageCards = globallyRankedCards
          .slice(pageStartIndex, pageStartIndex + pageSize)
          .map((card, index) => this.#prepareCard(card.cloneNode(true), page, index));

        const pageGrid = document.createElement('div');
        pageGrid.setAttribute('ref', 'grid');
        pageGrid.append(...pageCards);
        this.pages.set(page, pageGrid.outerHTML);
      }

      const currentPageStartIndex = (currentPage - 1) * pageSize;
      const currentPageCards = globallyRankedCards
        .slice(currentPageStartIndex, currentPageStartIndex + pageSize)
        .map((card, index) => this.#prepareCard(card.cloneNode(true), currentPage, index));

      const currentPageChildren =
        currentPage === 1
          ? this.#mergeCardsWithDecorativeItems(currentPageCards, pageOneData?.extras ?? [])
          : currentPageCards;

      grid.replaceChildren(...currentPageChildren);
    } catch (error) {
      console.error('Failed to globally rank out-of-stock results', error);
    }
  }

  #extractPageData(grid, page) {
    const children = Array.from(grid.children);
    const cards = [];
    const extras = [];
    let cardCount = 0;

    for (const child of children) {
      if (!(child instanceof HTMLElement)) continue;

      if (child.getAttribute('ref') === 'cards[]') {
        cards.push(child);
        cardCount += 1;
        continue;
      }

      extras.push({
        afterCardCount: cardCount,
        node: child.cloneNode(true),
      });
    }

    return {
      page,
      cards,
      extras,
    };
  }

  async #fetchGridForPage(page) {
    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('page', page.toString());
    pageUrl.hash = '';

    const pageHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl);
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');

    return parsedPage.querySelector('[ref="grid"]');
  }

  #getCurrentPage() {
    const pageParam = Number(new URL(window.location.href).searchParams.get('page'));

    if (Number.isFinite(pageParam) && pageParam > 0) {
      return pageParam;
    }

    const firstCard = this.refs.cards?.[0];
    const cardPage = Number(firstCard?.dataset.page);

    return Number.isFinite(cardPage) && cardPage > 0 ? cardPage : 1;
  }

  #mergeCardsWithDecorativeItems(cards, extras) {
    if (extras.length === 0) return cards;

    const mergedChildren = [];
    let extraIndex = 0;

    for (let cardIndex = 0; cardIndex <= cards.length; cardIndex += 1) {
      while (extraIndex < extras.length && extras[extraIndex].afterCardCount === cardIndex) {
        mergedChildren.push(extras[extraIndex].node);
        extraIndex += 1;
      }

      if (cardIndex < cards.length) {
        mergedChildren.push(cards[cardIndex]);
      }
    }

    return mergedChildren;
  }

  #prepareCard(card, page, index) {
    card.dataset.page = page.toString();
    card.className = `${card.className.replace(/\bproduct-grid__item--\d+\b/g, '').trim()} product-grid__item--${index}`.trim();
    return card;
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
